import React, { useCallback, useEffect, useState } from 'react';
import {
  Check, Copy, Globe, Loader2, Lock, LockOpen, Plus, RefreshCw, Trash2,
  TriangleAlert,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { notifySettingsUpdated } from '../context/SettingsContext';

/**
 * Ayarlar → Alan Adları.
 *
 * Kurulumun oyuncuya açık domaini sık değişir; sağlayıcı panellerine yazılan
 * callback adresleri ve lisansın tanıdığı adres her değişimde elle
 * güncellenmek zorunda kalıyordu. Bu ekran o bağı koparır: sabit alan adları
 * deftere eklenir, roller onlara atanır ve ayarlardaki adresler kendiliğinden
 * yeniden üretilir.
 *
 * Üç kural ekranda da görünür durur:
 *
 * - Adres **doğrulanmadan** role atanamaz (sunucu kendini o adresten çağırır).
 * - Dönüş adresleri her zaman yaşayan ana sitededir; role atanamaz.
 * - Bazı callback adresleri bizim kaydımızda değil sağlayıcının kendi
 *   panelindedir: onlar kopyalanabilir bir listede çıkar ve elle yazılır.
 */

const emptyState = {
  addresses: [], roles: [], manual_targets: [], locked_fields: [],
  panel_host: '', panel_locked: false, on_panel_host: false, current_host: '',
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '' : date.toLocaleDateString('tr-TR');
};

const certLabel = (value) => {
  if (!value) return '';
  const days = Math.round((new Date(value) - Date.now()) / 86400000);
  if (Number.isNaN(days)) return '';
  return days > 0
    ? `sertifika ${days} gün` : 'sertifika süresi dolmuş';
};

const SiteAddresses = () => {
  const { toast } = useToast();
  const [state, setState] = useState(emptyState);
  const [draft, setDraft] = useState({ host: '', label: '' });
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const absorb = useCallback((data) => {
    setState({ ...emptyState, ...data });
    const next = {};
    (data.roles || []).forEach((role) => { next[role.key] = role.host || ''; });
    setRoles(next);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/admin/addresses');
      absorb(data);
    } catch (error) {
      toast({
        title: 'Adres defteri okunamadı',
        description: error.response?.data?.detail || 'Bir hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [absorb, toast]);

  useEffect(() => { load(); }, [load]);

  const run = async (key, request, successTitle) => {
    setBusy(key);
    try {
      const { data } = await request();
      absorb(data);
      // Panel kabuğu ayrımı /settings yanıtından okur; kilit değişince
      // yenilenmezse arayüz eski moddan devam eder.
      notifySettingsUpdated();
      if (successTitle) toast({ title: successTitle });
      return data;
    } catch (error) {
      toast({
        title: 'İşlem tamamlanamadı',
        description: error.response?.data?.detail || 'Bir hata oluştu',
        variant: 'destructive',
      });
      return null;
    } finally {
      setBusy('');
    }
  };

  const addAddress = () => run(
    'add',
    () => client.post('/admin/addresses', {
      host: draft.host.trim(), label: draft.label.trim(),
    }),
    'Adres deftere eklendi',
  ).then((data) => { if (data) setDraft({ host: '', label: '' }); });

  const verify = async (row) => {
    const data = await run(
      `verify-${row.id}`,
      () => client.post(`/admin/addresses/${row.id}/verify`),
    );
    if (!data) return;
    if (data.verify?.ok) toast({ title: `${row.host} doğrulandı` });
    else {
      toast({
        title: `${row.host} doğrulanamadı`,
        description: data.verify?.error || '',
        variant: 'destructive',
      });
    }
  };

  const removeAddress = (row) => run(
    `del-${row.id}`,
    () => client.delete(`/admin/addresses/${row.id}`),
    'Adres defterden silindi',
  );

  const saveRoles = async () => {
    const payload = {};
    Object.entries(roles).forEach(([key, host]) => {
      if (host) payload[key] = host;
    });
    const data = await run(
      'roles',
      () => client.put('/admin/addresses/roles', { roles: payload }),
      'Roller kaydedildi ve adresler yeniden üretildi',
    );
    if (!data) return data;
    // Telegram webhook'u ayar kaydında durmaz; adres yalnızca sağlayıcıya
    // bildirilerek değişir. Kayıt başarısızsa bot eski adreste kalır ve
    // arıza sessizdir — bot susar, hata çıkmaz.
    const webhook = data.telegram_webhook;
    if (webhook && webhook.ok === false) {
      toast({
        title: 'Telegram webhook adresi güncellenemedi',
        description: `${webhook.error || 'Bilinmeyen hata'} — Ayarlar → `
          + 'Telegram sayfasından yeniden kaydedin, yoksa bot eski adreste '
          + 'kalır ve mesajlara yanıt vermez.',
        variant: 'destructive',
      });
    }
    return data;
  };

  const lock = () => run(
    'lock', () => client.post('/admin/addresses/panel-lock'),
    'Panel bu adrese kilitlendi',
  );

  const unlock = () => run(
    'unlock', () => client.delete('/admin/addresses/panel-lock'),
    'Panel kilidi açıldı',
  );

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: 'Kopyalandı' });
    } catch {
      toast({ title: 'Kopyalanamadı', variant: 'destructive' });
    }
  };

  const verifiedHosts = state.addresses.filter((row) => row.verified);
  const roleHost = (key) => roles[key] || '';
  const dirty = state.roles.some((role) => (role.host || '') !== roleHost(role.key));

  const header = (
    <div className="admin-page-header">
      <div>
        <h1>Alan Adları</h1>
        <p>
          Yönetim panelini, sağlayıcı bildirimlerini ve lisans kimliğini sabit
          bir alan adına bağlar. Defter boşken her şey bugünkü gibi yaşayan ana
          site adresinden çalışır.
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="admin-page">
        {header}
        <div className="admin-card admin-state">
          <Loader2 size={26} className="animate-spin text-slate-400" />
          <h2>Adres defteri yükleniyor…</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page flex flex-col gap-5">
      {header}
      <div className="admin-card address-card">
        <h2 className="admin-card-title">
          <Globe size={16} /> Adres defteri
        </h2>
        <p className="admin-card-hint">
          Kurulumun sahip olduğu sabit alan adları. Ana site domaini
          değiştiğinde bu adreslere dokunulmaz; sağlayıcı panellerine bir kez
          yazılır ve bir daha güncellenmez.
          <strong> Adres, doğrulanmadan hiçbir role atanamaz</strong> —
          doğrulama, kurulumun kendini o adresten çağırıp yanıtı tanımasıdır.
        </p>

        <div className="address-form">
          <div className="address-field">
            <label className="admin-label" htmlFor="address-host">Alan adı</label>
            <input
              id="address-host"
              className="admin-input"
              value={draft.host}
              onChange={(e) => setDraft({ ...draft, host: e.target.value })}
              placeholder="ornekadmin.com"
            />
          </div>
          <div className="address-field">
            <label className="admin-label" htmlFor="address-label">Not (isteğe bağlı)</label>
            <input
              id="address-label"
              className="admin-input"
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="Panel ve bildirimler"
            />
          </div>
          <button
            className="admin-btn admin-btn-primary"
            onClick={addAddress}
            disabled={busy === 'add' || !draft.host.trim()}
          >
            <Plus size={15} /> Ekle
          </button>
        </div>

        <div className="address-list">
          {state.addresses.length === 0 ? (
            <p className="admin-card-hint">
              Defter boş. Bu hâliyle panel, bildirimler ve lisans bugünkü gibi
              ana site adresinden çalışır.
            </p>
          ) : state.addresses.map((row) => (
            <div className="address-row" key={row.id}>
              <div>
                <div className="address-host">{row.host}</div>
                <div className="address-meta">
                  {row.verified ? (
                    <span className="address-pill address-pill-ok">
                      <Check size={12} /> doğrulandı
                    </span>
                  ) : (
                    <span className="address-pill address-pill-wait">
                      <TriangleAlert size={12} /> doğrulanmadı
                    </span>
                  )}
                  {row.cert_expires_at && ` · ${certLabel(row.cert_expires_at)}`}
                  {row.verified_at && ` · ${formatDate(row.verified_at)}`}
                  {row.roles?.length > 0 && ` · ${row.roles.length} rol`}
                  {row.label && ` · ${row.label}`}
                  {!row.verified && row.last_error && (
                    <div className="mt-1">{row.last_error}</div>
                  )}
                </div>
              </div>
              <div className="address-actions">
                <button
                  className="admin-btn admin-btn-secondary"
                  onClick={() => verify(row)}
                  disabled={busy === `verify-${row.id}`}
                >
                  <RefreshCw size={14} /> Doğrula
                </button>
                <button
                  className="admin-btn admin-btn-secondary"
                  onClick={() => removeAddress(row)}
                  disabled={busy === `del-${row.id}` || row.roles?.length > 0}
                  title={row.roles?.length > 0
                    ? 'Önce rollerini başka bir adrese taşıyın' : ''}
                >
                  <Trash2 size={14} /> Sil
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-card address-card">
        <h2 className="admin-card-title">Rol ataması</h2>
        <p className="admin-card-hint">
          Her rol için defterdeki doğrulanmış adreslerden biri seçilir. Boş
          bırakılan rol yaşayan ana siteden çalışır. Kaydedildiğinde ayar
          kaydındaki callback adresleri kendiliğinden yeniden üretilir.
        </p>

        <div className="address-role-grid">
          {state.roles.map((role) => (
            <div className="address-role" key={role.key}>
              <span className="address-role-label">{role.label}</span>
              <select
                className="admin-input"
                value={roleHost(role.key)}
                onChange={(e) => setRoles({ ...roles, [role.key]: e.target.value })}
              >
                <option value="">Ana site (varsayılan)</option>
                {verifiedHosts.map((row) => (
                  <option key={row.id} value={row.host}>{row.host}</option>
                ))}
              </select>
              <span className="address-role-note">{role.description}</span>
            </div>
          ))}
        </div>

        <p className="admin-card-hint">
          <strong>Dönüş adresleri role atanamaz</strong> ve listede yoktur:
          ödeme sonrası ve oyundan çıkışta oyuncunun tarayıcısı oraya düşer,
          sabit bir adrese düşerse yayındaki siteyi göremez.
        </p>

        <div className="address-actions-row">
          <button
            className="admin-btn admin-btn-primary"
            onClick={saveRoles}
            disabled={busy === 'roles' || !dirty}
          >
            Rolleri kaydet
          </button>
        </div>
      </div>

      {state.panel_host && (
        <div className="admin-card address-card">
          <h2 className="admin-card-title">
            {state.panel_locked ? <Lock size={16} /> : <LockOpen size={16} />}
            {' '}Panel kilidi
          </h2>
          <p className="admin-card-hint">
            {state.panel_locked ? (
              <>
                Yönetim paneli yalnızca <strong>{state.panel_host}</strong>
                {' '}adresinden açılıyor. Ana sitedeki /admin yolu hiçbir şey
                döndürmez.
              </>
            ) : (
              <>
                Panel rolü <strong>{state.panel_host}</strong> adresine atandı
                ama kapı henüz kapanmadı: panel iki adresten de açılıyor. Kapıyı
                kapatmak için kilidi <strong>o adresten</strong> onaylayın —
                kilit, panele oradan girebildiğinizin kanıtıyla kapanır.
              </>
            )}
          </p>
          <div className="address-actions-row">
            {state.panel_locked ? (
              <button
                className="admin-btn admin-btn-secondary"
                onClick={unlock}
                disabled={busy === 'unlock'}
              >
                <LockOpen size={14} /> Kilidi aç
              </button>
            ) : (
              <button
                className="admin-btn admin-btn-primary"
                onClick={lock}
                disabled={busy === 'lock' || !state.on_panel_host}
                title={state.on_panel_host
                  ? '' : `Bu işlemi ${state.panel_host} adresinden yapın`}
              >
                <Lock size={14} /> Bu adresi yönetim adresi olarak kilitle
              </button>
            )}
          </div>
        </div>
      )}

      {state.manual_targets.length > 0 && (
        <div className="admin-card address-card">
          <h2 className="admin-card-title">
            <TriangleAlert size={16} /> Sağlayıcı panelinde elle yazılacaklar
          </h2>
          <p className="admin-card-hint">
            Bu adresler bizim kaydımızda değil, sağlayıcının kendi panelinde
            durur; oradan güncellenmeden bildirimler eski adrese gelmeye devam
            eder. Taşıma bitene kadar <strong>eski adresi açık tutun</strong> —
            yolda olan bildirimler oraya düşer ve hangi adresten gelirse gelsin
            kabul edilir.
          </p>
          <div className="address-manual">
            {state.manual_targets.map((item) => (
              <div className="address-manual-item" key={item.key}>
                <div>
                  <div className="address-role-label">{item.label}</div>
                  <div className="address-url">{item.url}</div>
                  {item.note && (
                    <div className="address-role-note">{item.note}</div>
                  )}
                </div>
                <button
                  className="admin-btn admin-btn-secondary"
                  onClick={() => copy(item.url)}
                >
                  <Copy size={14} /> Kopyala
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteAddresses;
